import { Metamodel } from 'app/model/base/model';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import {
    IDataFlow,
    IDataFlowMappingTypeProperties,
    IDataFlowProperties,
    FLOW
} from 'app/model/adapters/dataflow/IDataflow';
import { Transformation } from 'app/model/dataflow/transformation';
import { DataFlowParameter } from 'app/model/dataflow/dataflow-parameter';
import { DataFlowFunction } from 'app/model/dataflow/dataflow-function';
import { Flowlet } from 'app/model/dataflow/flowlet';
import { DataFlowAdapter } from './dataflow.adapter';
import { InputTransformation } from 'app/model/dataflow/inputtransformation';
import { OutputTransformation } from 'app/model/dataflow/outputtransformation';
import { InputAdapter } from './input.adapter';
import { OutputAdapter } from './output.adapter';
import { ParseScriptMethod } from './flowlet.parser';
import { IDslScriptable } from 'app/model/adapters/dataflow/transformations/base-transformation.adapter';
import { BrowseTransformation } from 'app/model/dataflow/browsetransformation';
import { ConnectTransformation } from 'app/model/dataflow/connecttransformation';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { SinkAdapter } from './sink.adapter';
import { SourceAdapter } from './source.adapter';
import { DataFlowUtils } from 'app/authoring/component/transformations/dataFlow-utils';

export interface IScriptProperties {
    parameters: Set<DataFlowParameter>;
    functions: Set<DataFlowFunction>;
    transformations: Set<Transformation>;
}

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Flowlet),
    asynchronous: true
})
export class FlowletAdapter extends DataFlowAdapter {
    public async getDSL(dataflow: Flowlet): Promise<string> {
        const sourceAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(DataFlowSource)) as SourceAdapter;
        const sinkAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(DataFlowSink)) as SinkAdapter;
        const inputAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(InputTransformation)) as InputAdapter;
        const outputAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(OutputTransformation)) as OutputAdapter;
        const scripts: string[] = [];

        const hasBrowseOrConnectTransformation = !!dataflow.transformations.find(
            (transformation) =>
                transformation instanceof BrowseTransformation || transformation instanceof ConnectTransformation
        );

        const dataFlowFunctionsScript = this.getFunctionsDSL(dataflow.functions);
        if (dataFlowFunctionsScript) {
            scripts.push(dataFlowFunctionsScript);
        }
        const dataFlowParametersScript = this.getParametersDSL(dataflow.parameters);
        if (dataFlowParametersScript) {
            scripts.push(dataFlowParametersScript);
        }

        for (const input of dataflow.inputs) {
            scripts.push(await inputAdapter.getDslScript(input));
        }

        // TODO: Need to fix once the browse and connect transformations are added as a transformation in a data flow
        if (!hasBrowseOrConnectTransformation) {
            for (const source of dataflow.sources) {
                scripts.push(await sourceAdapter.getDslScript(source));
            }
        }

        for (const flowlet of dataflow.sourceFlowlets) {
            scripts.push(
                await (this.context.asyncAdapterFor(Metamodel.modelOf(flowlet)) as unknown as IDslScriptable).getDslScript(
                    flowlet
                )
            );
        }
        for (const transformation of dataflow.transformations) {
            scripts.push(
                await (
                    this.context.asyncAdapterFor(Metamodel.modelOf(transformation)) as unknown as IDslScriptable
                ).getDslScript(transformation)
            );
        }
        for (const sink of dataflow.sinks) {
            scripts.push(await sinkAdapter.getDslScript(sink));
        }
        for (const output of dataflow.outputs) {
            scripts.push(await outputAdapter.getDslScript(output));
        }

        return scripts.join('\n');
    }

    public getPreviewDSL(dataflow: Flowlet): string {
        const inputAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(InputTransformation)) as InputAdapter;
        const outputAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(OutputTransformation)) as OutputAdapter;
        const scripts: string[] = [];

        dataflow.inputs.forEach((input) => scripts.push(inputAdapter.getMemoryStoreDSL(input)));

        const props: string[] = [];
        dataflow.inputs.forEach((input) => {
            const mappingStrings = input.schema.map((column) => DataFlowUtils.escapeFieldIfNeeded(column.name));
            props.push(`mapColumn(\n\t\t${mappingStrings.join(',\n\t\t')}\n\t)`);
        });
        props.push(`composition: '${dataflow.name}'`);

        const inputStreams: string[] = [...dataflow.inputs].map((input: InputTransformation) => input.name);
        const transformationData = props.filter((prop) => !!prop).join(',\n\t');
        const outputStreams: string[] = [...dataflow.outputs].map((output: OutputTransformation) => output.name);

        const composeTransform =
            `${inputStreams.join(', ')} compose(${transformationData}) ` +
            `${FLOW} ${dataflow.name}@(${outputStreams.join(', ')})`.trimLeft();

        scripts.push(composeTransform);

        dataflow.outputs.forEach((output) =>
            scripts.push(`${dataflow.name}@${output.name} ${outputAdapter.getCacheStoreDSL(output)}`)
        );

        return scripts.join('\n');
    }

    public toPreviewResource(flowlet: Flowlet): IDataFlow {
        const resource = {
            name: flowlet.name + 'dataflow',
            properties: {
                type: 'MappingDataFlow',
                typeProperties: {
                    sources: [...flowlet.inputs].map((input) => {
                        return {
                            name: input.name
                        };
                    }),
                    transformations: [
                        {
                            name: flowlet.name,
                            flowlet: {
                                referenceName: flowlet.name,
                                type: 'DataFlowReference'
                            }
                        }
                    ],
                    sinks: [...flowlet.outputs].map((output) => {
                        return {
                            name: output.name
                        };
                    }),
                    script: this.getPreviewDSL(flowlet)
                }
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return resource as any;
    }

    protected async _toModel(resource: IDataFlow, model?: Flowlet): Promise<Flowlet> {
        if (!model) {
            model = Flowlet.MODEL.create<Flowlet>();
        }

        model = (await super.parseJSONToModel(resource, model)) as Flowlet;
        await model.parseDslAsync(this.context, resource, {
            method: ParseScriptMethod.PARAMETERS
        });
        return model;
    }

    protected async _toResource(flowlet: Flowlet): Promise<IDataFlow> {
        const inputAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(InputTransformation)) as InputAdapter;
        const outputAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(OutputTransformation)) as OutputAdapter;
        const resource = (await super._toResource(flowlet)) as IDataFlow;

        if (resource && resource.properties) {
            (resource.properties as IDataFlowProperties).type = 'Flowlet';
            (
                (resource.properties as IDataFlowProperties).typeProperties as IDataFlowMappingTypeProperties
            ).transformations = (
                (resource.properties as IDataFlowProperties).typeProperties as IDataFlowMappingTypeProperties
            ).transformations
                .concat(await Promise.all(Array.from(flowlet.inputs).map((input) => inputAdapter.toResource(input))))
                .concat(
                    await Promise.all(Array.from(flowlet.outputs).map((output) => outputAdapter.toResource(output)))
                );
        }

        // todo: this should be handled in dataflow adapter, and will be removed once feature flag is opened there
        if (this._isCombinedScriptEnabled && !!flowlet.savedWithSingleScript) {
            (
                (resource.properties as IDataFlowProperties).typeProperties as IDataFlowMappingTypeProperties
            ).scriptLines = (
                (resource.properties as IDataFlowProperties).typeProperties as IDataFlowMappingTypeProperties
            ).script
                .replace(/\t/g, '     ')
                .split(/\n/g);
            delete ((resource.properties as IDataFlowProperties).typeProperties as IDataFlowMappingTypeProperties)
                .script;
        }

        return resource;
    }
}
